import type {
  AssignmentActionInput,
  AssignmentContainer,
  AudienceCount,
  AudienceCountInput,
  Group,
  NewEntityId,
  NotificationCreateInput,
  NotificationUpdateInput,
  QueryAssignmentsArgs,
  QueryListWorkflowsArgs,
  QueryWorkflowResultGroupsArgs,
  ResultGroupContainer,
  ResultGroupUpdateInput,
  Segmentation,
  SegmentationContainer,
  User,
  UserContainer,
  Workflow,
  WorkflowContainer,
  WorkflowCreateInput,
  WorkflowOverview,
  WorkflowResultGroup,
  WorkflowStats,
  WorkflowTriggerData,
  WorkflowUpdateInput,
} from '@/client/services/api/graphql/gql/graphql';
import {
  archiveAssignmentMutation,
  markAssignmentCompletedMutation,
  sendAssignmentReminderMutation,
} from './mutations/workflowActionsMutations';
import {
  createWorkflowNotificationMutation,
  updateWorkflowNotificationMutation,
} from './mutations/notifications/workflowNotificationMutations';

import { createWorkflowMutation } from './mutations/createWorkflowMutation';
import { getAudienceCountQuery } from './queries/getAudienceCountQuery';
import { getWorkflowDetailsQuery } from './queries/getWorkflowDetailsQuery';
import { getWorkflowOverviewTotalsQuery } from './queries/getWorkflowOverviewTotals';
import { getWorkflowResultGroupDetailsQuery } from './queries/getWorkflowResultGroupDetailsQuery';
import { getWorkflowStatsQuery } from './queries/getWorkflowStatsQuery';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { listAssignmentRecipientsQuery } from './queries/listAssignmentRecipientsQuery';
import { listGroupsQuery } from './queries/listGroupsQuery';
import { listWorkflowResultGroupsQuery } from './queries/listWorkflowResultGroupsQuery';
import { listWorkflowsQuery } from '@/client/components/admin/assignments/api/queries/listWorkflowsQuery';
import { searchSegmentationsQuery } from './queries/searchSegmentationsQuery';
import { searchUsersQuery } from './queries/searchUsersQuery';
import { triggerPointInTimeWorkflowMutation } from './mutations/triggerPointInTimeWorkflow';
import { updateWorkflowMutation } from './mutations/updateWorkflowMutation';
import { updateWorkflowResultGroupMutation } from './mutations/updateWorkflowResultGroupMutation';

const getWorkflowOverviewTotals = async (): Promise<WorkflowOverview> => {
  const data: any = await graphQLClient.request(getWorkflowOverviewTotalsQuery);

  return data.workflowOverview;
};

const listWorkflows = async (options: QueryListWorkflowsArgs): Promise<WorkflowContainer> => {
  const data: any = await graphQLClient.request(listWorkflowsQuery, options);

  return data.listWorkflows;
};

const listAssignmentRecipients = async (
  options: QueryAssignmentsArgs,
): Promise<AssignmentContainer> => {
  const data: any = await graphQLClient.request(listAssignmentRecipientsQuery, options);

  return data.assignments;
};

const listWorkflowInstances = async (
  options: QueryWorkflowResultGroupsArgs,
): Promise<ResultGroupContainer> => {
  const data: any = await graphQLClient.request(listWorkflowResultGroupsQuery, options);

  return data.workflowResultGroups;
};

const getWorkflowResultGroupDetails = async (
  id: string | undefined,
): Promise<WorkflowResultGroup> => {
  const data: any = await graphQLClient.request(getWorkflowResultGroupDetailsQuery, {
    id,
  });

  return data.workflowResultGroup;
};

const updateWorkflowResultGroup = async (payload: {
  id: string | undefined;
  resultGroupUpdateInput: ResultGroupUpdateInput;
}): Promise<NewEntityId> => {
  const { id, resultGroupUpdateInput } = payload;

  const data: any = await graphQLClient.request(updateWorkflowResultGroupMutation, {
    id,
    resultGroupUpdateInput,
  });

  return data.updateResultGroup;
};

const archiveAssignments = async (payload: AssignmentActionInput): Promise<boolean> => {
  const data: any = await graphQLClient.request(archiveAssignmentMutation, {
    archiveAssignmentInput: payload,
  });

  return data.archiveAssignments;
};

const markAssignmentsCompleted = async (payload: AssignmentActionInput): Promise<boolean> => {
  const data: any = await graphQLClient.request(markAssignmentCompletedMutation, {
    completeAssignmentInput: payload,
  });

  return data.completeAssignments;
};

const sendAssignmentsReminder = async (payload: AssignmentActionInput): Promise<boolean> => {
  const data: any = await graphQLClient.request(sendAssignmentReminderMutation, {
    assignmentReminderInput: payload,
  });

  return data.sendAssignmentReminder;
};

const getWorkflowDetails = async (id: string | undefined): Promise<Workflow> => {
  const data: any = await graphQLClient.request(getWorkflowDetailsQuery, {
    id,
  });

  return data.getWorkflow;
};

const getWorkflowStats = async (id: string | undefined): Promise<WorkflowStats> => {
  const data: any = await graphQLClient.request(getWorkflowStatsQuery, {
    id,
  });

  return data.workflowStats;
};

const audienceSearch = async (
  searchTerm: string,
  includeGroups: boolean,
): Promise<{ users: User[]; segmentations: Segmentation[]; groups: Group[] }> => {
  let groups: any = [];

  const users: { users: UserContainer } = await graphQLClient.request(searchUsersQuery, {
    name: { contains: searchTerm },
    suspended: { eq: false },
  });

  const segmentations: { segmentations: SegmentationContainer } = await graphQLClient.request(
    searchSegmentationsQuery,
    {
      name: { contains: searchTerm },
      segmentationType: { in: ['location', 'department', 'team', 'grade', 'org_level'] },
    },
  );

  if (includeGroups) {
    const groupsData: any = await graphQLClient.request(listGroupsQuery, {
      name: { contains: searchTerm },
    });

    groups = groupsData.listSegmentationGroups.edges.map((edge: any) => edge.node);
  }

  return {
    users: users.users.edges.map((edge) => edge.node),
    segmentations: segmentations.segmentations.edges.map((edge) => edge.node),
    groups,
  };
};

const createWorkflow = async (workflow: WorkflowCreateInput): Promise<NewEntityId> => {
  const data: any = await graphQLClient.request(createWorkflowMutation, {
    workflow,
  });

  return data.createWorkflow;
};

const updateWorkflow: any = async (
  id: string,
  workflowUpdateInput: WorkflowUpdateInput,
): Promise<NewEntityId> => {
  const data: any = await graphQLClient.request(updateWorkflowMutation, {
    id,
    workflowUpdateInput,
  });

  return data.updateWorkflow;
};

const triggerPointInTimeWorkflow = async (
  workflowTriggerData: WorkflowTriggerData,
): Promise<boolean> => {
  const data: any = await graphQLClient.request(triggerPointInTimeWorkflowMutation, {
    workflowTriggerData,
  });

  return data.triggerPointInTimeWorkflow;
};

const createWorkflowNotification = async (
  notification: NotificationCreateInput,
): Promise<NewEntityId> => {
  const data: any = await graphQLClient.request(createWorkflowNotificationMutation, {
    notification,
  });

  return data.createNotification;
};

const updateWorkflowNotification = async (
  id: string,
  notification: NotificationUpdateInput,
): Promise<NewEntityId> => {
  const data: any = await graphQLClient.request(updateWorkflowNotificationMutation, {
    id,
    notification,
  });

  return data.updateNotification;
};

const deactivateWorkflow = async (workflowId: string | undefined): Promise<boolean> => {
  const data: any = await graphQLClient.request(updateWorkflowMutation, {
    id: workflowId,
    workflowUpdateInput: {
      enabled: false,
    },
  });

  return data.updateWorkflow;
};

const activateWorkflow = async (workflowId: string | undefined): Promise<boolean> => {
  const data: any = await graphQLClient.request(updateWorkflowMutation, {
    id: workflowId,
    workflowUpdateInput: {
      enabled: true,
    },
  });

  return data.updateWorkflow;
};

const getAssignmentAudienceCount = async (audience: AudienceCountInput[]): Promise<number> => {
  const data: any = await graphQLClient.request(getAudienceCountQuery, {
    audience,
  });

  return data.audienceCount.totalUsers;
};

const AdminAssignmentsService = {
  getWorkflowOverviewTotals,
  listWorkflows,
  listAssignmentRecipients,
  listWorkflowInstances,
  getWorkflowResultGroupDetails,
  updateWorkflowResultGroup,
  archiveAssignments,
  sendAssignmentsReminder,
  markAssignmentsCompleted,
  getWorkflowDetails,
  getWorkflowStats,
  audienceSearch,
  createWorkflow,
  updateWorkflow,
  triggerPointInTimeWorkflow,
  createWorkflowNotification,
  updateWorkflowNotification,
  deactivateWorkflow,
  activateWorkflow,
  getAssignmentAudienceCount,
};

export default AdminAssignmentsService;
